import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Clock,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { EntityTableTab } from "@/components/shared/EntityTableTab";
import { NewProductDrawer } from "@/modules/products/components/NewProductDrawer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { formatDateTime } from "@/lib/utils";
import {
  useCompany,
  useCompanyList,
  useCompanySubEntity,
  useDeleteCompany,
  useUpdateCompany,
} from "../hooks/useCompanies";
import { TimelineTab } from "@/components/shared/TimelineTab";
import { NewCompanyDrawer } from "../components/NewCompanyDrawer";
import { NewProjectDrawer } from "@/modules/projects/components/NewProjectDrawer";
import { NewTaskDrawer } from "@/modules/tasks/components/NewTaskDrawer";
import { NewActionDrawer } from "@/modules/actions/components/NewActionDrawer";
import { NewMeetingDrawer } from "@/modules/meetings/components/NewMeetingDrawer";
import { NewMilestoneDrawer } from "@/modules/milestones/components/NewMilestoneDrawer";
import { NotesTab } from "@/modules/notes/components/NotesTab";
import { IdeasTab } from "@/modules/ideas/components/IdeasTab";
import { DecisionsTab } from "@/modules/decisions/components/DecisionsTab";
import { BudgetsTabContent } from "@/modules/budgets/components/BudgetsTabContent";
import { ExpensesTabContent } from "@/modules/expenses/components/ExpensesTabContent";
import { EntityDocumentsTab } from "@/modules/media/components/EntityDocumentsTab";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { useCompanyVisibility } from "../useCompanyVisibility";
import {
  ExternalContactsTab,
  ExternalExpensesTab,
  ExternalMeetingsTab,
  ExternalProjectsTab,
  ExternalProposalsTab,
} from "../components/ExternalCompanyTabs";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { EntityTeamTab } from "@/modules/teams/components/EntityTeamTab";
import { useTeam } from "@/modules/teams/hooks/useTeam";
import type { CompanyCategory, CompanyDetail } from "../types";

type Tab =
  | "overview"
  | "subsidiaries"
  | "products"
  | "projects"
  | "meetings"
  | "tasks"
  | "actions"
  | "notes"
  | "ideas"
  | "milestones"
  | "decisions"
  | "budgets"
  | "expenses"
  | "contacts"
  | "proposals"
  | "team"
  | "documents"
  | "timeline";

const TABS: { key: Tab; label: string; counted?: boolean }[] = [
  { key: "overview", label: "Overview" },
  { key: "subsidiaries", label: "Subsidiaries", counted: true },
  { key: "products", label: "Products" },
  { key: "projects", label: "Projects" },
  { key: "meetings", label: "Meetings", counted: true },
  { key: "tasks", label: "Tasks", counted: true },
  { key: "actions", label: "Actions", counted: true },
  { key: "notes", label: "Notes", counted: true },
  { key: "ideas", label: "Ideas" },
  { key: "milestones", label: "Milestones" },
  { key: "decisions", label: "Decisions" },
  { key: "budgets", label: "Budgets" },
  { key: "expenses", label: "Expenses" },
  { key: "contacts", label: "Contacts" },
  { key: "proposals", label: "Proposals" },
  { key: "team", label: "Team", counted: true },
  { key: "documents", label: "Documents" },
  { key: "timeline", label: "Timeline" },
];

const TYPE_BADGE_COLORS: Record<string, { bg: string; fg: string }> = {
  PARENT:     { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  SUBSIDIARY: { bg: "hsl(38,92%,92%)",  fg: "hsl(38,92%,30%)"   },
  PARTNER:    { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)"  },
  CLIENT:     { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)"  },
  CUSTOMER:   { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)"  },
  VENDOR:     { bg: "hsl(20,85%,92%)",  fg: "hsl(20,85%,38%)"   },
  INVESTOR:   { bg: "hsl(45,90%,92%)",  fg: "hsl(40,90%,32%)"   },
};

const NEUTRAL_BADGE = { bg: "hsl(210,30%,94%)", fg: "hsl(215,20%,35%)" };

function TypeBadge({
  type,
  labelOverride,
}: {
  type: string;
  labelOverride?: string;
}) {
  const c = TYPE_BADGE_COLORS[type] ?? NEUTRAL_BADGE;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.fg }}
    >
      {labelOverride ?? type}
    </span>
  );
}

function TypeBadges({
  types,
  labelByValue,
}: {
  types: string[];
  labelByValue: Map<string, string>;
}) {
  if (types.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <TypeBadge key={t} type={t} labelOverride={labelByValue.get(t)} />
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const active = status === "ACTIVE";
  const planned = status === "PLANNED";
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        active
          ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
          : planned
            ? { background: "hsl(210,80%,93%)", color: "hsl(210,80%,35%)" }
            : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {active ? "Active" : planned ? "Planned" : "Inactive"}
    </span>
  );
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">{value || "—"}</div>
    </div>
  );
}

export default function CompanyDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: company, isLoading, isError } = useCompany(id);
  const deleteCompany = useDeleteCompany();
  const updateCompany = useUpdateCompany(id);

  const [tab, setTab] = useState<Tab>("overview");
  const [editMode, setEditMode] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [subsidiaryDrawerOpen, setSubsidiaryDrawerOpen] = useState(false);
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [projectDrawerOpen, setProjectDrawerOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false);
  const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
  const [milestoneDrawerOpen, setMilestoneDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Phase 1+2: which tabs and stat cards are visible. Drives the entire
  // External-vs-Operational layout — see useCompanyVisibility for the rules.
  const visibility = useCompanyVisibility(company);

  // company_type dropdown is shared by the header badges and the Overview
  // tab's display. The Overview tab also queries it under a category filter
  // for the edit-mode multi-select.
  const { data: headerTypeOptions = [] } = useCategoryItems("company_type");
  const headerLabelByValue = new Map(
    headerTypeOptions.map((t) => [t.value, t.label]),
  );

  // If the user lands on (or was previously on) a tab that's not allowed for
  // the company's category, fall back to overview.
  useEffect(() => {
    if (visibility.visibleTabs.length === 0) return;
    if (!visibility.visibleTabs.includes(tab as never)) {
      setTab("overview");
    }
  }, [visibility.visibleTabs, tab]);

  // Sub-entity queries (only fetched when tab is active)
  const products = useCompanySubEntity<any>(id, "products", tab === "products" ? { page: 1, limit: 50 } : {});
  const projects = useCompanySubEntity<any>(id, "projects", tab === "projects" ? { page: 1, limit: 50 } : {});
  const meetings = useCompanySubEntity<any>(id, "meetings", tab === "meetings" ? { page: 1, limit: 50 } : {});
  // For External companies, the Meetings stat card needs to reflect the
  // attendee-based reverse-link count (Meeting → MeetingAttendee → Contact →
  // Contact.companyId), not the direct Meeting.companyId count which is
  // always 0 for External companies. Cheap separate query — limit 1, we only
  // need pagination.total.
  const externalMeetingsCount = useMeetingList({
    attendeeCompanyId: id,
    limit: 1,
    enabled: visibility.isExternal,
  });
  const tasks = useCompanySubEntity<any>(id, "tasks", tab === "tasks" ? { page: 1, limit: 50 } : {});
  const actions = useCompanySubEntity<any>(id, "actions", tab === "actions" ? { page: 1, limit: 50 } : {});
  const milestones = useCompanySubEntity<any>(id, "milestones", tab === "milestones" ? { page: 1, limit: 50 } : {});
  // Subsidiaries: company list scoped to parentId = this company. Always
  // queried (cheap; indexed by parentId) so the tab content is ready
  // without a flash. The hide-when-empty rule uses
  // company._count.subsidiaries on the detail DTO.
  const subsidiaries = useCompanyList({ parentId: id, limit: 50 });
  // Team is operational-only — gate the query so external companies don't
  // hit the API (the backend would reject anyway).
  const teamQuery = useTeam(
    visibility.isExternal ? null : "COMPANY",
    visibility.isExternal ? null : id,
  );
  const teamCount = teamQuery.data?.length ?? 0;

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          <span className="text-sm text-muted-foreground">
            Loading company…
          </span>
        </div>
      </AppLayout>
    );
  }

  if (isError || !company) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-destructive">
          Failed to load company.
        </div>
      </AppLayout>
    );
  }

  function handleDelete() {
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    await deleteCompany.mutateAsync(id);
    navigate("/companies");
  }

  const meetingsCount = visibility.isExternal
    ? (externalMeetingsCount.data?.pagination.total ?? 0)
    : company._count.meetings;

  const subsidiariesCount = company._count.subsidiaries ?? 0;

  const tabCount = (key: Tab): string | undefined => {
    if (key === "meetings") return String(meetingsCount);
    if (key === "tasks") return String(company._count.tasks);
    if (key === "actions") return String(company._count.actions);
    if (key === "team") return String(teamCount);
    if (key === "subsidiaries") return String(subsidiariesCount);
    return undefined;
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Companies", href: "/companies" },
        { label: company.name },
      ]}
    >
      <div className="w-full space-y-6">
        {/* Toast */}
        {toast && (
          <div className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg" style={{ borderColor: "hsl(142,55%,80%)" }}>
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/companies")}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Building2 className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">
              View Company
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="History"
              onClick={() => setHistoryOpen(true)}
            >
              <Clock className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Edit"
              onClick={() => {
                setEditMode((v) => !v);
                if (editMode) setTab("overview");
              }}
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Delete"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Company header card */}
        <Card className="w-full">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <h2
                  className="font-bold tracking-tight"
                  style={{ fontSize: 20, fontWeight: 700 }}
                >
                  {company.name}
                </h2>
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  <ReferenceIdBadge id={company.code} />
                  <StatusBadge status={company.status} />
                  <TypeBadges
                    types={company.types}
                    labelByValue={headerLabelByValue}
                  />
                  <span className="text-xs text-muted-foreground">
                    {company.currency}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab bar */}
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4 overflow-x-auto">
            {TABS.filter((t) =>
              visibility.visibleTabs.includes(t.key as never),
            )
              // Subsidiaries tab is only meaningful for companies with at
              // least one child; suppress the empty placeholder so the
              // tab row isn't cluttered. Users create subsidiaries via
              // Companies → New (parentId set) or the "+ New Subsidiary"
              // button that appears once the tab is visible.
              .filter((t) => t.key !== "subsidiaries" || subsidiariesCount > 0)
              .map((t) => {
              const count = tabCount(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                    tab === t.key
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                  {t.counted && count && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      ({count})
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        {tab === "overview" && (
          <OverviewTab
            company={company}
            editMode={editMode}
            onSave={async (payload) => {
              await updateCompany.mutateAsync(payload);
              setEditMode(false);
              showToast("Company updated successfully");
            }}
            onCancel={() => setEditMode(false)}
            isSaving={updateCompany.isPending}
          />
        )}
        {tab === "subsidiaries" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setSubsidiaryDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Subsidiary
              </Button>
            </div>
            <EntityTableTab
              data={subsidiaries.data?.data ?? []}
              isLoading={subsidiaries.isLoading && !subsidiaries.data}
              columns={["name", "code", "status"]}
              emptyText="No subsidiaries added yet"
              onRowClick={(row) => navigate(`/companies/${row.id}`)}
            />
          </div>
        )}
        {tab === "products" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setProductDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Product
              </Button>
            </div>
            <EntityTableTab
              data={products.data?.data ?? []}
              isLoading={products.isLoading && !products.data}
              columns={["name", "code", "status", "projectCount"]}
              emptyText="No products added yet"
              onRowClick={(row) => navigate(`/products/${row.id}`)}
            />
          </div>
        )}
        {tab === "projects" &&
          (visibility.isExternal ? (
            <ExternalProjectsTab companyId={company.id} />
          ) : (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setProjectDrawerOpen(true)}>
                  <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                  New Project
                </Button>
              </div>
              <EntityTableTab
                data={projects.data?.data ?? []}
                isLoading={projects.isLoading && !projects.data}
                columns={["name", "code", "type", "productName", "status", "progress"]}
                emptyText="No projects found"
                onRowClick={(row) => navigate(`/projects/${row.id}`)}
              />
            </div>
          ))}
        {tab === "meetings" &&
          (visibility.isExternal ? (
            <ExternalMeetingsTab companyId={company.id} />
          ) : (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setMeetingDrawerOpen(true)}>
                  <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                  New Meeting
                </Button>
              </div>
              <EntityTableTab
                data={meetings.data?.data ?? []}
                isLoading={meetings.isLoading && !meetings.data}
                columns={["title", "status", "scheduledAt", "organizer", "entityContext"]}
                emptyText="No meetings found"
                onRowClick={(row) => navigate(`/meetings/${row.id}`)}
              />
            </div>
          ))}
        {tab === "tasks" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setTaskDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Task
              </Button>
            </div>
            <EntityTableTab
              data={tasks.data?.data ?? []}
              isLoading={tasks.isLoading && !tasks.data}
              columns={["title", "priority", "productName", "projectName", "status", "dueDate", "assignee"]}
              emptyText="No tasks found"
              onRowClick={(row) => navigate(`/tasks/${row.id}`)}
            />
          </div>
        )}
        {tab === "actions" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setActionDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Action
              </Button>
            </div>
            <EntityTableTab
              data={actions.data?.data ?? []}
              isLoading={actions.isLoading && !actions.data}
              columns={["title", "priority", "source", "productName", "projectName", "status", "dueDate", "assignee"]}
              emptyText="No actions found"
              onRowClick={(row) => navigate(`/actions/${row.id}`)}
            />
          </div>
        )}
        {tab === "notes" && (
          <NotesTab
            filter={{ companyId: company.id }}
            prefill={{ companyId: company.id, locked: { companyId: true } }}
          />
        )}
        {tab === "milestones" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setMilestoneDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Milestone
              </Button>
            </div>
            <EntityTableTab
              data={milestones.data?.data ?? []}
              isLoading={milestones.isLoading && !milestones.data}
              columns={["title", "status", "progress", "dueDate", "entityContext"]}
              emptyText="No milestones found"
              onRowClick={(row) => navigate(`/milestones/${row.id}`)}
            />
          </div>
        )}
        {tab === "ideas" && (
          <IdeasTab
            filter={{ companyId: company.id }}
            prefill={{ companyId: company.id, locked: { companyId: true } }}
          />
        )}
        {tab === "decisions" && (
          <DecisionsTab
            filter={{ companyId: company.id }}
            prefill={{ companyId: company.id }}
          />
        )}
        {tab === "budgets" && <BudgetsTabContent companyId={company.id} />}
        {tab === "expenses" &&
          (visibility.isExternal ? (
            <ExternalExpensesTab companyId={company.id} />
          ) : (
            <ExpensesTabContent companyId={company.id} />
          ))}
        {tab === "contacts" && <ExternalContactsTab companyId={company.id} />}
        {tab === "proposals" && <ExternalProposalsTab companyId={company.id} />}
        {tab === "team" && (
          <EntityTeamTab entityType="COMPANY" entityId={company.id} />
        )}
        {tab === "documents" && (
          <EntityDocumentsTab
            entityType="COMPANY"
            entityId={company.id}
            entityLabel={company.name}
            defaultView="tree"
          />
        )}
        {tab === "timeline" && <TimelineTab entityType="company" entityId={id} />}

        {/* Drawers */}
        <ActivityHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="company"
          entityId={company.id}
          entityLabel={company.name}
        />
        <NewCompanyDrawer
          isOpen={subsidiaryDrawerOpen}
          onClose={() => setSubsidiaryDrawerOpen(false)}
          parentId={id}
        />
        <NewProductDrawer
          isOpen={productDrawerOpen}
          onClose={() => setProductDrawerOpen(false)}
          companyId={id}
        />
        <NewProjectDrawer
          isOpen={projectDrawerOpen}
          onClose={() => setProjectDrawerOpen(false)}
          companyId={id}
        />
        <NewTaskDrawer
          isOpen={taskDrawerOpen}
          onClose={() => setTaskDrawerOpen(false)}
          prefill={{ companyId: id, locked: { companyId: true } }}
          onCreated={() => setToast("Task created")}
        />
        <NewActionDrawer
          isOpen={actionDrawerOpen}
          onClose={() => setActionDrawerOpen(false)}
          prefill={{ companyId: id, locked: { companyId: true } }}
          onCreated={() => setToast("Action created")}
        />
        <NewMeetingDrawer
          isOpen={meetingDrawerOpen}
          onClose={() => setMeetingDrawerOpen(false)}
          onCreated={(code) => showToast(`Meeting created: ${code}`)}
        />
        <NewMilestoneDrawer
          isOpen={milestoneDrawerOpen}
          onClose={() => setMilestoneDrawerOpen(false)}
          prefill={{ companyId: id, locked: { companyId: true } }}
          onCreated={(code) => showToast(`Milestone created: ${code}`)}
        />

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete company?"
          description={`Are you sure you want to delete '${company.name}'? It will be removed from your Companies list.`}
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}

// ── Overview Tab with Edit Mode ──────────────────────────
type EditFormState = {
  name: string;
  shortName: string;
  legalName: string;
  types: string[];
  category: CompanyCategory;
  status: "PLANNED" | "ACTIVE" | "INACTIVE";
  industry: string;
  currency: string;
  website: string;
  email: string;
  phone: string;
  address: string;
  registrationNumber: string;
  taxNumber: string;
};

function initForm(c: CompanyDetail): EditFormState {
  return {
    name: c.name,
    shortName: c.shortName ?? "",
    legalName: c.legalName ?? "",
    types: [...c.types],
    category: c.category,
    status: c.status,
    industry: c.industry ?? "",
    currency: c.currency,
    website: c.website ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    address: c.address ?? "",
    registrationNumber: c.registrationNumber ?? "",
    taxNumber: c.taxNumber ?? "",
  };
}

function OverviewTab({
  company,
  editMode,
  onSave,
  onCancel,
  isSaving,
}: {
  company: CompanyDetail;
  editMode: boolean;
  onSave: (payload: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [form, setForm] = useState<EditFormState>(() => initForm(company));

  // Reset form when company data changes or edit mode toggles
  useEffect(() => {
    setForm(initForm(company));
  }, [company, editMode]);

  // Type options scoped to the company's current category. Editing the types
  // is restricted to swaps within the same category — the API enforces this
  // and rejects with 400 on mismatch.
  const { data: typeOptions = [] } = useCategoryItems(
    "company_type",
    undefined,
    { category: form.category },
  );
  // All categories' labels (Operational + External). Needed for display,
  // since a saved company may have types whose dropdown rows are excluded
  // by the category filter above (shouldn't happen, but is defensive).
  const { data: allTypes = [] } = useCategoryItems("company_type");
  const typeLabelByValue = new Map(allTypes.map((t) => [t.value, t.label]));

  function set<K extends keyof EditFormState>(field: K, value: EditFormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleType(value: string) {
    setForm((prev) => ({
      ...prev,
      types: prev.types.includes(value)
        ? prev.types.filter((v) => v !== value)
        : [...prev.types, value],
    }));
  }

  async function handleSave() {
    if (!form.name.trim() || form.types.length === 0) return;
    await onSave({
      name: form.name.trim(),
      shortName: form.shortName.trim() || null,
      legalName: form.legalName.trim() || null,
      types: form.types,
      category: form.category,
      status: form.status,
      industry: form.industry.trim() || null,
      currency: form.currency,
      website: form.website.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      registrationNumber: form.registrationNumber.trim() || null,
      taxNumber: form.taxNumber.trim() || null,
    });
  }

  return (
    <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-10">
      {/* Left 70% */}
      <div className="space-y-6 lg:col-span-7">
        <Card>
          <CardContent className="space-y-5 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Basic Information
            </h3>
            {editMode ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">
                      Company Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="edit-name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-shortName">Short Name</Label>
                    <Input
                      id="edit-shortName"
                      value={form.shortName}
                      onChange={(e) => set("shortName", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-legalName">Legal Name</Label>
                    <Input
                      id="edit-legalName"
                      value={form.legalName}
                      onChange={(e) => set("legalName", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Code</Label>
                    <Input value={company.code} disabled className="bg-muted font-mono" />
                    <p className="text-[11px] text-muted-foreground">
                      Code is auto-generated and immutable.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-category">Category</Label>
                    <Select
                      id="edit-category"
                      value={form.category}
                      onChange={(e) => {
                        const next = e.target.value as CompanyCategory;
                        // Clear types so the user explicitly re-picks ones
                        // valid for the new category — avoids server-side
                        // mismatch errors on submit.
                        setForm((prev) => ({
                          ...prev,
                          category: next,
                          types: [],
                        }));
                      }}
                    >
                      <option value="OPERATIONAL">Operational (Our)</option>
                      <option value="EXTERNAL">External</option>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>
                      Types <span className="text-destructive">*</span>
                    </Label>
                    <div
                      className="flex flex-wrap gap-2 rounded-md border p-2"
                      style={{ borderColor: "hsl(210,30%,85%)" }}
                    >
                      {typeOptions.length === 0 && (
                        <span className="text-xs text-muted-foreground">
                          No types configured for this category.
                        </span>
                      )}
                      {typeOptions
                        .filter((t) => t.isActive || form.types.includes(t.value))
                        .map((t) => (
                          <label
                            key={t.id}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/40"
                          >
                            <Checkbox
                              checked={form.types.includes(t.value)}
                              onCheckedChange={() => toggleType(t.value)}
                            />
                            <span className="text-sm">{t.label}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select
                      id="edit-status"
                      value={form.status}
                      onChange={(e) => set("status", e.target.value as "PLANNED" | "ACTIVE" | "INACTIVE")}
                    >
                      <option value="PLANNED">Planned</option>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-industry">Industry</Label>
                    <Input
                      id="edit-industry"
                      value={form.industry}
                      onChange={(e) => set("industry", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-currency">Currency</Label>
                    <Select
                      id="edit-currency"
                      value={form.currency}
                      onChange={(e) => set("currency", e.target.value)}
                    >
                      <option value="AED">AED — UAE Dirham</option>
                      <option value="USD">USD — US Dollar</option>
                      <option value="GBP">GBP — British Pound</option>
                      <option value="EUR">EUR — Euro</option>
                      <option value="INR">INR — Indian Rupee</option>
                      <option value="SAR">SAR — Saudi Riyal</option>
                      <option value="QAR">QAR — Qatari Riyal</option>
                      <option value="KWD">KWD — Kuwaiti Dinar</option>
                    </Select>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <FieldRow label="Company Name" value={company.name} />
                  <FieldRow label="Short Name" value={company.shortName} />
                  <FieldRow label="Legal Name" value={company.legalName} />
                  <FieldRow label="Company Code" value={company.code} />
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Types
                    </div>
                    <TypeBadges
                      types={company.types}
                      labelByValue={typeLabelByValue}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Status
                    </div>
                    <StatusBadge status={company.status} />
                  </div>
                  <FieldRow label="Industry" value={company.industry} />
                  <FieldRow label="Currency" value={company.currency} />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-5 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Contact Information
            </h3>
            {editMode ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-website">Website</Label>
                  <Input
                    id="edit-website"
                    value={form.website}
                    onChange={(e) => set("website", e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="info@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="+971..."
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <FieldRow label="Website" value={company.website} />
                <FieldRow label="Email" value={company.email} />
                <FieldRow label="Phone" value={company.phone} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-5 pt-6">
            <h3 className="text-sm font-semibold text-foreground">Address</h3>
            {editMode ? (
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <textarea
                  id="edit-address"
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  rows={3}
                  className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Full address"
                />
              </div>
            ) : (
              <FieldRow label="Address" value={company.address} />
            )}
          </CardContent>
        </Card>

        {/* Save/Cancel buttons (only in edit mode) */}
        {editMode && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !form.name.trim()}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Save className="h-4 w-4" strokeWidth={1.5} />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Right 30% */}
      <div className="space-y-6 lg:col-span-3">
        <Card>
          <CardContent className="space-y-5 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Legal / Registration
            </h3>
            {editMode ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-regNum">Registration Number</Label>
                  <Input
                    id="edit-regNum"
                    value={form.registrationNumber}
                    onChange={(e) => set("registrationNumber", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-taxNum">Tax/VAT Number</Label>
                  <Input
                    id="edit-taxNum"
                    value={form.taxNumber}
                    onChange={(e) => set("taxNumber", e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <FieldRow
                  label="Registration Number"
                  value={company.registrationNumber}
                />
                <FieldRow label="Tax/VAT Number" value={company.taxNumber} />
              </>
            )}
          </CardContent>
        </Card>

        {company.parent && (
          <Card>
            <CardContent className="space-y-3 pt-6">
              <h3 className="text-sm font-semibold text-foreground">
                Parent Company
              </h3>
              <a
                href={`/companies/${company.parent.id}`}
                className="text-sm font-medium text-primary hover:underline"
              >
                {company.parent.name}
              </a>
              <div className="font-mono text-xs text-muted-foreground">
                {company.parent.code}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="space-y-4 pt-6">
            <h3 className="text-sm font-semibold text-foreground">Dates</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Clock
                  className="mt-0.5 h-4 w-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Created At
                  </div>
                  <div className="text-foreground">
                    {formatDateTime(company.createdAt)}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock
                  className="mt-0.5 h-4 w-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Updated At
                  </div>
                  <div className="text-foreground">
                    {formatDateTime(company.updatedAt)}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

